import { Download } from 'lucide-react'
import type { Statement, StatementLine } from '@shared/types'
import { Badge } from '@/components/ui/badge'
import { Table, THead, Th, Tr, Td, TotalsRow } from '@/components/ui/data-table'
import { Term } from '@/components/ui/term'
import { fmtDate, fmtDateTime, fmtMoney, fmtPeriod } from '@/lib/format'

const SECTIONS: { key: StatementLine['section']; title: React.ReactNode }[] = [
  { key: 'licence_fees', title: <><Term id="licence-fee">Licence Fees</Term>, in advance</> },
  { key: 'top_ups', title: 'Pro-rata Licence Fees for the period just gone' },
  { key: 'expansion', title: <><Term id="expansion-charge">Expansion Charges</Term>, in arrears</> },
  { key: 'adjustments', title: 'Adjustments' },
]

const fmtRange = (r: { from: string; to: string }) =>
  `${fmtDate(r.from)} – ${fmtDate(new Date(Date.parse(r.to) - 1).toISOString())} (UTC)`

/** One brand's statement for a Billing Period, as Atarim ops invoices from it. */
export function StatementView({ statement: s, downloadBase }: { statement: Statement; downloadBase: string }) {
  const foreign = s.currency !== 'USD'
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-border bg-card p-5 shadow-sm">
        <div className="space-y-1 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold">{fmtPeriod(s.period)}</span>
            {s.status === 'closed' ? <Badge variant="active">Final</Badge> : <Badge variant="pending">Provisional</Badge>}
          </div>
          <div className="text-muted-foreground">Billing Period {fmtRange(s.periodRange)}</div>
          <div className="text-muted-foreground">
            Licence Fees cover {fmtPeriod(s.aheadPeriod)}: {fmtRange(s.aheadRange)}
          </div>
          <div className="text-muted-foreground">
            {s.status === 'closed' ? `Closed ${fmtDateTime(s.closedAt)} · ` : ''}Statement date {fmtDate(s.invoiceDate)} · Payment due{' '}
            {fmtDate(s.dueDate)}
          </div>
          {s.fx && (
            <div className="text-muted-foreground">
              1 {s.currency} = {s.fx.rate.toFixed(6)} USD, rate of {fmtDate(s.fx.date)} ({s.fx.source})
              {s.status !== 'closed' && ' — fixed when the period closes'}
            </div>
          )}
        </div>
        <div className="space-y-1 text-right text-sm">
          <div className="font-medium">{s.partner.billing.entity || s.partner.name}</div>
          {s.partner.billing.address && <div className="text-muted-foreground">{s.partner.billing.address}</div>}
          {s.partner.billing.country && <div className="text-muted-foreground">{s.partner.billing.country}</div>}
          {s.partner.billing.vatNumber && <div className="text-muted-foreground">VAT {s.partner.billing.vatNumber}</div>}
          {s.partner.billing.reverseCharge && <div className="text-muted-foreground">Reverse charge applies</div>}
          <div className="flex justify-end gap-3 pt-2">
            <a className="inline-flex items-center gap-1 font-medium text-ata-light-blue hover:underline" href={`${downloadBase}.pdf`}>
              <Download className="size-3.5" /> PDF
            </a>
            <a className="inline-flex items-center gap-1 font-medium text-ata-light-blue hover:underline" href={`${downloadBase}.csv`}>
              <Download className="size-3.5" /> CSV
            </a>
          </div>
        </div>
      </div>

      <Table>
        <THead>
          <Th>Item</Th>
          {foreign && <Th right>Amount ({s.currency})</Th>}
          <Th right>Amount (USD)</Th>
        </THead>
        <tbody>
          {SECTIONS.map(({ key, title }) => {
            const rows = s.lines.filter((l) => l.section === key)
            if (!rows.length) return null
            return [
              <Tr key={key}>
                <Td colSpan={foreign ? 3 : 2} className="bg-muted/50 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {title}
                </Td>
              </Tr>,
              ...rows.map((l, i) => (
                <Tr key={`${key}-${i}`}>
                  <Td>
                    {l.description}
                    {l.relatesTo && key !== 'licence_fees' && (
                      <span className="ml-2">
                        <Badge variant="expiring">Relates to {fmtPeriod(l.relatesTo)}</Badge>
                      </span>
                    )}
                  </Td>
                  {foreign && <Td right>{l.currency === 'USD' ? '—' : fmtMoney(l.amountMinor, l.currency)}</Td>}
                  <Td right>{fmtMoney(l.amountUsdMinor, 'USD')}</Td>
                </Tr>
              )),
            ]
          })}
          <TotalsRow>
            <Td>Total due</Td>
            {foreign && <Td right />}
            <Td right>{fmtMoney(s.totals.totalUsdMinor, 'USD')}</Td>
          </TotalsRow>
        </tbody>
      </Table>
    </div>
  )
}
